/**
 * @module WorkTango Reporting App
 *
 * This React app has all its pieces connected together here in this file and
 * then attached to the DOM. This should be the only place in the whole app
 * that ever touches the DOM directly. This is where the user interface is loaded
 * and wrapped in the Redux provider/store, along with the main theme. An
 * initial "restore session" action is immediately dispatched to start that
 * process and try to recover any lingering session data. From here, it's all
 * up to the other modules to do their thing.
 */

import './wdyr';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { appQueryClient } from './adapters/reactQuery';
import { store } from '~/behaviors/store';
import { ReduxProvider } from '~/behaviors/ReduxProvider';
import * as languages from '~/behaviors/languages';
import { accountActions } from '~/behaviors/actions/account.actions';
import { initCache as initApolloCache, client as apollo } from '~/adapters/worktango/apollo';
import theme from './config/theme';
import './index.css';
import { initTelemetry } from './telemetry/datadog_tracer';
import UserInterface from './views';
import { initializeFeatureFlagProvider, OpenFeatureProvider } from '@worktango/feature-flags-react';

// WARNING: This must match the exact ThemeProvider expected from the shared-frontend
// in order for it to work locally using `npm link`. A different styling engine's
// provider will not be seen by shared-frontend and resets the theme there.
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

// DEPRECATED: This is only needed by legacy attribute filter, should be removed after refactor
import { ApolloProvider } from '@apollo/client';
import { AnyAction } from 'redux';
import { CustomErrorBoundary } from './views/UI.shared/CustomErrorBoundary';
import { devCycleClientSdkKey, devCycleDebuggerEnabled } from './config/settings/app.settings';

// Initialize telemetry (e.g. Datadog)
initTelemetry();

languages.init();

// Initialize the apollo cache's persistence module.
initApolloCache().catch((e) => e);

// Triggers initialization actions when app first loads which will check
// if there is an existing auth token in local storage first and recover user
// data from it to recover the account from the last logged-in user. If there is
// no token to recover, the subsequent initialization action should simply be
// rejected and the user will need to log in.
store.dispatch(accountActions.restoreAccount() as unknown as AnyAction);

initializeFeatureFlagProvider({
  sdkKey: devCycleClientSdkKey as string,
  shouldEnableDebugger: devCycleDebuggerEnabled,
  options: {
    disableAutomaticEventLogging: false,
  },
});

const container = document.getElementById('worktango');
const root = createRoot(container!);
root.render(
  <CustomErrorBoundary>
    <OpenFeatureProvider>
      <ReduxProvider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <ApolloProvider client={apollo}>
              <QueryClientProvider client={appQueryClient}>
                <UserInterface />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </ApolloProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </ReduxProvider>
    </OpenFeatureProvider>
  </CustomErrorBoundary>,
);
